import { useEffect, useState } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { FileText, Sparkles, Link as LinkIcon } from "lucide-react";
import api from "../../../lib/api";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short" }); }
  catch { return iso; }
};

/**
 * Lists the last few finance documents whose products were pushed into
 * Inventory — either automatically on upload or via the manual CTA.
 * Gives the owner full traceability of what came in and when.
 */
export function RecentAutoEntriesCard({ restaurantId, onOpenDocument }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    api.listRecentAutoInventoryEntries(restaurantId, 8)
      .then((r) => { if (!cancelled) setEntries(r?.entries || []); })
      .catch(() => { if (!cancelled) setEntries([]); });
    return () => { cancelled = true; };
  }, [restaurantId]);

  if (!entries || entries.length === 0) return null;

  return (
    <Card data-testid="recent-auto-entries-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-emerald-500" />
          <h3 className="font-semibold text-sm">Últimas entradas automáticas</h3>
          <span className="text-[11px] text-muted-foreground ml-auto">
            Desde tus facturas
          </span>
        </div>
        <ul className="divide-y max-h-80 overflow-y-auto pr-1">
          {entries.map((e) => (
            <li
              key={e.document_id}
              className="flex items-center gap-3 py-2 text-xs cursor-pointer hover:bg-muted/50 px-2 -mx-2 rounded"
              onClick={() => onOpenDocument?.(e.document_id)}
              data-testid={`auto-entry-${e.document_id}`}
            >
              <div className="shrink-0 h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {e.supplier_name || e.filename || "Documento"}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {e.matched_count > 0 && (
                    <span>{e.matched_count} actualizados</span>
                  )}
                  {e.matched_count > 0 && e.created_count > 0 && <span> · </span>}
                  {e.created_count > 0 && (
                    <span>{e.created_count} creados</span>
                  )}
                  {(e.matched_count === 0 && e.created_count === 0) && (
                    <span>{e.movements} mov.</span>
                  )}
                  <span className="mx-1">·</span>
                  <span>{fmtDate(e.synced_at)}</span>
                </div>
              </div>
              {e.total_amount != null && (
                <div className="shrink-0 text-right">
                  <div className="font-semibold tabular-nums">{fmtEUR(e.total_amount)}</div>
                  {e.invoice_number && (
                    <div className="text-[10px] text-muted-foreground">#{e.invoice_number}</div>
                  )}
                </div>
              )}
              <LinkIcon className="w-3 h-3 text-muted-foreground shrink-0" />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default RecentAutoEntriesCard;
